import { useEffect, useMemo, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Bell, Loader2 } from "lucide-react";

export default function SalesPush() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("general");
  const [audience, setAudience] = useState("all");
  const [q, setQ] = useState("");
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/platform/tenants").then(({ data }) => setTenants(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return tenants.slice(0, 8);
    return tenants.filter((t) => `${t.name} ${t.slug} ${t.owner_email}`.toLowerCase().includes(needle)).slice(0, 8);
  }, [q, tenants]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/sales/push", {
        title: title.trim(),
        body: body.trim(),
        kind,
        tenant_id: audience === "one" ? tenantId : null,
      });
      toast.success(`Sent to ${data.stored || 0} stores · ${data.pushed || 0} phones via push`);
      setTitle("");
      setBody("");
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="sales-push">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Bell className="w-6 h-6 text-teal-600" /> App notifications</h1>
      <p className="text-sm text-slate-500 mt-1">Send a push to merchants who installed the Mera Startup app. New-order tune and the general tune are different sounds on the phone.</p>
      <Card className="mt-5 p-5 border-slate-200">
        <form onSubmit={send} className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input required data-testid="sales-push-title" className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Your store is live" />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea required data-testid="sales-push-body" className="mt-1" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Open the app to see what changed." />
          </div>
          <div>
            <Label>Sound</Label>
            <div className="mt-2 flex gap-2">
              <button type="button" data-testid="sales-push-kind-general" onClick={() => setKind("general")} className={`h-10 px-4 rounded-full text-sm font-semibold border ${kind === "general" ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-700"}`}>General tune</button>
              <button type="button" data-testid="sales-push-kind-order" onClick={() => setKind("order")} className={`h-10 px-4 rounded-full text-sm font-semibold border ${kind === "order" ? "bg-amber-500 text-slate-900 border-amber-500" : "bg-white text-slate-700"}`}>New order tune</button>
            </div>
          </div>
          <div>
            <Label>Audience</Label>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => setAudience("all")} className={`h-10 px-4 rounded-full text-sm font-semibold border ${audience === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white"}`}>Everyone with the app</button>
              <button type="button" onClick={() => setAudience("one")} className={`h-10 px-4 rounded-full text-sm font-semibold border ${audience === "one" ? "bg-slate-900 text-white border-slate-900" : "bg-white"}`}>One store</button>
            </div>
            {audience === "one" ? (
              <div className="mt-3">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search store name or email" data-testid="sales-push-search" />
                <div className="mt-2 space-y-1">
                  {hits.map((t) => (
                    <button type="button" key={t.id} onClick={() => setTenantId(t.id)} className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${tenantId === t.id ? "border-teal-500 bg-teal-50" : "border-slate-200"}`}>
                      <span className="font-semibold">{t.name}</span>
                      <span className="text-slate-500"> · {t.owner_email || t.slug}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <Button type="submit" disabled={sending || (audience === "one" && !tenantId)} data-testid="sales-push-send" className="bg-teal-600 hover:bg-teal-500">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send notification"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
